import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import Alert from "../Alert";
import { sendGetRequest } from "../../utils";

const ROWS_PER_PAGE = 25;
const LOAD_ERROR = "Failed to load messages. Please try again.";

const formatDate = (date) => (date ? new Date(date).toLocaleString() : "");

function Messages() {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  // Search and page live in the URL (?q=marie&page=2) so a refresh keeps them
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || "";
  const page = Math.max(0, (parseInt(searchParams.get("page"), 10) || 1) - 1);

  const updateParams = (changes, options) => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      for (const [name, value] of Object.entries(changes)) {
        if (value) next.set(name, value);
        else next.delete(name);
      }
      return next;
    }, options);
  };

  const doFetchMessages = () => {
    sendGetRequest("messages", true)
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data)) {
          setError(data.errMessage || LOAD_ERROR);
          return;
        }
        setError(null);
        setData(data);
      })
      .catch(() => setError(LOAD_ERROR));
  };

  useEffect(() => {
    doFetchMessages();
  }, []);

  const query = searchQuery.trim().toLowerCase();
  const filteredData = query
    ? data.filter((row) =>
        [row.subscriber_name, row.to, row.body].some(
          (field) => field && field.toLowerCase().includes(query)
        )
      )
    : data;

  const lastPage = Math.max(0, Math.ceil(filteredData.length / ROWS_PER_PAGE) - 1);
  const currentPage = Math.min(page, lastPage);
  const visibleData = filteredData.slice(
    currentPage * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE + ROWS_PER_PAGE
  );

  return (
    <div>
      <Box sx={{ paddingTop: 2, flexGrow: 1 }} className="shadow-lg border">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert error={error} containerClassName="mt-0 mb-2" />
            <TableContainer
              component={Paper}
              className="!border-none !shadow-none"
            >
              <Typography variant="h4" className="p-4">
                Sent Messages
              </Typography>
              <div className="flex items-center p-4 gap-x-4">
                <div>
                  <span className="mr-1">Search:</span>
                  <input
                    type="text"
                    placeholder="Name, phone, text"
                    className="border p-1 rounded-md border-gray-400 min-w-48"
                    value={searchQuery}
                    onChange={(e) =>
                      updateParams(
                        { q: e.target.value, page: "" },
                        { replace: true }
                      )
                    }
                  />
                </div>
                <div>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={doFetchMessages}
                  >
                    Refresh
                  </Button>
                </div>
              </div>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell className="!font-semibold">Sent</TableCell>
                    <TableCell className="!font-semibold">To</TableCell>
                    <TableCell className="!font-semibold">Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleData.length > 0 ? (
                    <>
                      {visibleData.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell size="small" className="whitespace-nowrap">
                            {formatDate(row.date)}
                          </TableCell>
                          <TableCell size="small" className="whitespace-nowrap">
                            <div>
                              {row.is_owner ? "Owner" : row.subscriber_name || "—"}
                            </div>
                            <div className="text-xs text-gray-600">{row.to}</div>
                          </TableCell>
                          <TableCell size="small" className="break-words">
                            {row.body}
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} size="medium">
                        <div className="text-center">No record found.</div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={filteredData.length}
                page={currentPage}
                onPageChange={(event, newPage) =>
                  updateParams({ page: newPage > 0 ? String(newPage + 1) : "" })
                }
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
              />
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}

export default Messages;
